<script setup lang="ts">
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import Divider from '@/components/common/divider.vue';
import InsightAdjustButton from '@/components/home/insights/insight-adjust-button.vue';
import InsightProfileModal from '@/components/home/insights/insight-profile-modal.vue';
import InsightRegenerateBanner from '@/components/home/insights/insight-regenerate-banner.vue';
import InsightsGenerationError from '@/components/home/insights/insights-generation-error.vue';
import InsightsGenerationProgress from '@/components/home/insights/insights-generation-progress.vue';
import InsightsProfileReview from '@/components/home/insights/insights-profile-review.vue';
import InsightsTabs from '@/components/home/insights/insights-tabs.vue';
import { useInsightsContainer } from '@/components/home/insights/use-insights-container';
import { formatDateNoYear } from '@/utils/formatters';
import InsightsProfile from './insights-profile.vue';

const {
  hasLoaded,
  userProfileEnabled,
  hasProfile,
  personalSurveyProfile,
  canAdjustHints,
  isProfileModalOpen,
  isSavingProfile,
  hasProfileSaveError,
  saveProfile,
  onSuggestionApply,
  onSuggestionRefresh,
  hasSetupPersonalNewsOrFavoriteThemes,
  hasPersonalNewsArticles,
  hasFeaturedArticles,
  payload,
  canOpen,
  isOpen,
  toggleOpen,
  onProfileSaved,
  onRegenerate,
  retry,
  hasGenerationError,
  hasRegenerationError,
  showRegenerateBanner,
  dismiss,
  progressBarWidth,
  durationLabel,
  showProgress,
  isComplete,
  errorTitle,
  errorMessage,
} = useInsightsContainer({ pageName: 'home' });

const dateStr = formatDateNoYear(dayjs());
</script>

<template>
  <div v-if="hasLoaded && userProfileEnabled" class="insights-wrapper">
    <div class="insights-container">
      <div class="title">
        <DgrIcon name="tipss" :keep-fill="false" />
        <div class="c-title-bold--xm">{{ dateStr }} の発想のヒント (β)</div>
        <template v-if="hasProfile && canOpen">
          <button
            class="c-btn c-btn--small c-btn--auto"
            :class="isOpen ? 'c-outlineBtn' : 'c-btn--AnewsPrimary'"
            @click="toggleOpen"
          >
            {{ isOpen ? '閉じる' : '開く' }}
          </button>
        </template>
      </div>
      <div class="subtitle">
        <DgrIcon name="aconnect-logo" size="small" />
        <div class="c-text-regular--m bubble">
          「<span
            v-tooltip.bottom="{
              content:
                'パーソナルニュースとお気に入りテーマに基づく情報をお届けします。',
            }"
            >あなたの関心</span
          >」「<span
            v-tooltip.bottom="{
              content: '注目記事をもとに整理した情報をお届けします。',
            }"
            >組織の関心</span
          >」「<span
            v-tooltip.bottom="{
              content:
                'あなたや組織の関心領域では見つけにくい、異分野の知見をお届けします。',
            }"
            >関心の外</span
          >」から、気になるものを覗いてみてください。
        </div>
      </div>
      <InsightsProfile v-if="!hasProfile" @profile-saved="onProfileSaved" />
      <template v-else>
        <InsightsGenerationProgress
          v-if="showProgress"
          :progress-bar-width="progressBarWidth"
          :duration-label="durationLabel"
          :is-complete="isComplete"
        />
        <template v-else-if="isOpen">
          <InsightsGenerationError
            v-if="hasGenerationError"
            :title="errorTitle"
            :message="errorMessage"
            @retry="retry"
          />
          <template v-else>
            <InsightsGenerationError
              v-if="hasRegenerationError"
              title="発想のヒントの再生成に失敗しました"
              :message="errorMessage"
              @retry="retry"
            />
            <InsightRegenerateBanner
              v-else-if="showRegenerateBanner"
              @regenerate="onRegenerate"
              @close="dismiss"
            />
            <div class="saved-link">
              <InsightAdjustButton
                v-if="canAdjustHints"
                @click="isProfileModalOpen = true"
              />
              <RouterLink
                :to="{ name: 'savedInsights' }"
                class="c-btn c-btn--small c-btn--auto c-outlineBtn"
              >
                <DgrIcon
                  size="small"
                  name="bookmark-fill"
                  :keep-fill="false"
                  class="saved-link-icon"
                />
                <div>保存済み一覧</div>
              </RouterLink>
            </div>
            <InsightsTabs
              :payload="payload"
              :has-setup-personal-news-or-favorite-themes="
                hasSetupPersonalNewsOrFavoriteThemes
              "
              :has-personal-news-articles="hasPersonalNewsArticles"
              :has-featured-articles="hasFeaturedArticles"
            />
            <Divider class="full-bleed" margin="0 -24px 0 -24px" />
            <InsightsProfileReview />
          </template>
        </template>
      </template>
    </div>
  </div>
  <InsightProfileModal
    v-if="personalSurveyProfile"
    v-model:open="isProfileModalOpen"
    variant="adjust"
    :profile="personalSurveyProfile"
    :is-submitting="isSavingProfile"
    :has-save-error="hasProfileSaveError"
    @save="saveProfile"
    @suggestion-apply="onSuggestionApply"
    @suggestion-refresh="onSuggestionRefresh"
  />
</template>
<style lang="scss" scoped>
.insights-wrapper {
  box-sizing: border-box;
  max-width: 944px;
  margin: 0 auto;
}
.insights-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 912px;
  margin: 0 16px;
  padding: 24px;
  border-radius: 0 0 4px 4px;
  background-color: #fff;
  border: 1px solid $color-green400;
  border-top: 0;

  .title {
    display: flex;
    gap: 2px;
    align-items: center;

    button {
      margin-left: auto;
    }
  }
  .saved-link {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    .saved-link-icon {
      color: $color-green600;
    }
  }
  .subtitle {
    display: flex;
    gap: 12px;
    align-items: center;

    .bubble {
      position: relative;
      padding: 2px 8px;
      border-radius: 4px;
      background: $color-gray200;

      &:after {
        content: '';
        position: absolute;
        display: block;
        width: 0;
        border-style: solid;
        border-color: transparent $color-gray200;
        border-width: 4px 6px 4px 0;
        top: 50%;
        left: -6px;
        margin-top: -4px;
      }

      span {
        cursor: default;
        text-decoration: underline;
      }
    }
  }

  .full-bleed {
    width: auto;
  }
}
</style>
